/**
 * Mon Espace Analytics Service
 *
 * Tracks:
 *  - Navigation within Mon Espace (tab switches, dropdown opens)
 *  - Feature adoption (profile updates, alert creation, preference saves)
 *  - Notification interactions (read, delete, bulk actions, category filters)
 *  - Conversion funnels (notification → property view → appointment)
 *
 * All helpers are silent-fail: analytics errors never break the app.
 */

const GA_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID;

// ─── Internal gtag helper ─────────────────────────────────────────────────────

const gtag = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  } catch (_) {
    // silent fail
  }
};

const fire = (eventName, params = {}) => {
  if (!GA_ID) return;
  gtag('event', eventName, {
    event_category: 'Mon Espace',
    send_to: GA_ID,
    ...params,
  });
};

// ─── Navigation Tracking ──────────────────────────────────────────────────────

/**
 * Track Mon Espace dropdown open in header
 */
export const trackMonEspaceDropdownOpen = () => {
  fire('mon_espace_dropdown_open', {
    event_label: 'header_dropdown',
  });
};

/**
 * Track navigation to a Mon Espace section
 * @param {string} section  e.g. 'notifications', 'user-profile', 'account-dashboard'
 * @param {string} source   e.g. 'header_dropdown', 'mobile_menu', 'direct'
 */
export const trackMonEspaceNavigation = (section, source = 'header_dropdown') => {
  fire('mon_espace_navigate', {
    event_label: section,
    section,
    navigation_source: source,
  });
};

/**
 * Track tab switch within a Mon Espace page
 * @param {string} page   e.g. 'user-profile', 'account-dashboard'
 * @param {string} tab    e.g. 'saved', 'alerts', 'appointments', 'security'
 */
export const trackMonEspaceTabSwitch = (page, tab) => {
  fire('mon_espace_tab_switch', {
    event_label: `${page}__${tab}`,
    page,
    tab,
  });
};

// ─── Feature Adoption Tracking ────────────────────────────────────────────────

/**
 * Track profile update saved
 * @param {string} field  e.g. 'full_name', 'phone', 'password', 'avatar'
 */
export const trackProfileUpdate = (field) => {
  fire('mon_espace_profile_update', {
    event_label: field,
    updated_field: field,
  });
};

/**
 * Track alert creation
 * @param {{ commune_names: string[], property_types: string[], notify_email: boolean, notify_sms: boolean }} alertData
 */
export const trackAlertCreated = (alertData = {}) => {
  fire('mon_espace_alert_created', {
    event_label: 'new_alert',
    alert_communes: (alertData?.commune_names || [])?.join(', '),
    alert_types: (alertData?.property_types || [])?.join(', '),
    notify_email: alertData?.notify_email ? 'yes' : 'no',
    notify_sms: alertData?.notify_sms ? 'yes' : 'no',
  });
};

/**
 * Track alert toggle (activate / deactivate)
 * @param {boolean} isActive  new state after toggle
 */
export const trackAlertToggle = (isActive) => {
  fire('mon_espace_alert_toggle', {
    event_label: isActive ? 'activated' : 'deactivated',
    alert_state: isActive ? 'active' : 'inactive',
  });
};

/**
 * Track notification preferences saved
 * @param {{ frequency: string, channels: string[] }} prefs
 */
export const trackNotificationPreferencesSaved = (prefs = {}) => {
  fire('mon_espace_preferences_saved', {
    event_label: prefs?.frequency || 'unknown',
    digest_frequency: prefs?.frequency || '',
    channels: (prefs?.channels || [])?.join(', '),
  });
};

/**
 * Track security tab action
 * @param {string} action  'password_changed' | 'session_revoked' | 'history_viewed'
 */
export const trackSecurityAction = (action) => {
  fire('mon_espace_security_action', {
    event_label: action,
    security_action: action,
  });
};

// ─── Notification Interaction Tracking ───────────────────────────────────────

/**
 * Track notification center page load with initial unread count
 * @param {number} unreadCount
 */
export const trackNotificationCenterOpen = (unreadCount = 0) => {
  fire('mon_espace_notifications_open', {
    event_label: 'notifications_center',
    unread_count: unreadCount,
  });
};

/**
 * Track category filter selection in notifications center
 * @param {string} category  e.g. 'all', 'inquiries', 'viewings', 'saved', 'alerts'
 */
export const trackNotificationCategoryFilter = (category) => {
  fire('mon_espace_notification_filter', {
    event_label: category,
    notification_category: category,
  });
};

/**
 * Track single notification marked as read
 * @param {string} notificationType  e.g. 'property_inquiry', 'appointment_confirmation'
 */
export const trackNotificationRead = (notificationType) => {
  fire('mon_espace_notification_read', {
    event_label: notificationType || 'unknown',
    notification_type: notificationType || '',
  });
};

/**
 * Track notification deleted
 * @param {string} notificationType
 */
export const trackNotificationDeleted = (notificationType) => {
  fire('mon_espace_notification_deleted', {
    event_label: notificationType || 'unknown',
    notification_type: notificationType || '',
  });
};

/**
 * Track bulk action on notifications
 * @param {'mark_read'|'delete'} action
 * @param {number} count  number of notifications affected
 */
export const trackNotificationBulkAction = (action, count = 0) => {
  fire('mon_espace_notification_bulk', {
    event_label: action,
    bulk_action: action,
    affected_count: count,
  });
};

// ─── Conversion Funnel Tracking ───────────────────────────────────────────────

/**
 * Track notification → property view funnel entry
 * Fired when user clicks a notification that leads to a property
 * @param {string} notificationType
 * @param {string} propertyId
 */
export const trackNotificationToPropertyFunnel = (notificationType, propertyId) => {
  fire('mon_espace_funnel_notification_to_property', {
    event_label: notificationType || 'unknown',
    notification_type: notificationType || '',
    property_id: propertyId || '',
    funnel_stage: 'notification_clicked',
  });
};

/**
 * Track wishlist → property view funnel entry
 * Fired when user clicks "View" on a saved property
 * @param {string} propertyId
 */
export const trackWishlistToPropertyFunnel = (propertyId) => {
  fire('mon_espace_funnel_wishlist_to_property', {
    event_label: propertyId || 'unknown',
    property_id: propertyId || '',
    funnel_stage: 'wishlist_view_clicked',
  });
};

/**
 * Track alert → property discovery funnel
 * Fired when a triggered alert notification is opened
 * @param {string} alertId
 */
export const trackAlertToDiscoveryFunnel = (alertId) => {
  fire('mon_espace_funnel_alert_to_discovery', {
    event_label: alertId || 'unknown',
    alert_id: alertId || '',
    funnel_stage: 'alert_triggered',
  });
};

/**
 * Track friction point — user abandons Mon Espace without completing action
 * @param {string} page   page where friction occurred
 * @param {string} reason optional label
 */
export const trackFrictionPoint = (page, reason = '') => {
  fire('mon_espace_friction_point', {
    event_label: reason || page,
    friction_page: page,
    friction_reason: reason,
  });
};

export default {
  trackMonEspaceDropdownOpen,
  trackMonEspaceNavigation,
  trackMonEspaceTabSwitch,
  trackProfileUpdate,
  trackAlertCreated,
  trackAlertToggle,
  trackNotificationPreferencesSaved,
  trackSecurityAction,
  trackNotificationCenterOpen,
  trackNotificationCategoryFilter,
  trackNotificationRead,
  trackNotificationDeleted,
  trackNotificationBulkAction,
  trackNotificationToPropertyFunnel,
  trackWishlistToPropertyFunnel,
  trackAlertToDiscoveryFunnel,
  trackFrictionPoint,
};
